import * as AlertDialogPrimitive from "@radix-ui/react-alert-dialog";
import type * as React from "react";
import { cn } from "../../lib/utils";
import { buttonVariants } from "./button";

/**
 * Potwierdzenie działania nieodwracalnego.
 *
 * Zastępuje `window.confirm`, i nie chodzi o wygląd. Okno systemowe blokuje cały
 * wątek przeglądarki, nie da się w nim nazwać przycisków („OK" wobec „Usuń plik
 * na zawsze"), część przeglądarek pozwala je wyłączyć na stałe dla witryny,
 * a treści nie da się sformatować ani pokazać w niej, co konkretnie zniknie.
 *
 * `AlertDialog`, nie `Dialog`: różnica jest w semantyce dla czytnika ekranu
 * (`role="alertdialog"`) i w tym, że tego okna **nie zamyka kliknięcie poza
 * obszarem** — przy pytaniu o rzecz nieodwracalną przypadkowe zamknięcie nie może
 * być łatwiejsze od odpowiedzi.
 */

export const AlertDialog = AlertDialogPrimitive.Root;
export const AlertDialogTrigger = AlertDialogPrimitive.Trigger;

export function AlertDialogContent({
  className,
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Content>) {
  return (
    <AlertDialogPrimitive.Portal>
      {/* `data-slot` niesie wygaszenie tła z `styles.css`. */}
      <AlertDialogPrimitive.Overlay
        data-slot="dialog-overlay"
        className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm"
      />
      <AlertDialogPrimitive.Content
        data-slot="dialog-content"
        className={cn(
          "fixed top-1/2 left-1/2 z-50 w-[calc(100%-2rem)] max-w-[28rem] -translate-x-1/2 -translate-y-1/2",
          "rounded-xl border border-border bg-popover p-5 text-popover-foreground shadow-2xl shadow-black/50",
          "flex flex-col gap-3",
          className,
        )}
        {...props}
      />
    </AlertDialogPrimitive.Portal>
  );
}

export function AlertDialogTitle({
  className,
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Title>) {
  return (
    <AlertDialogPrimitive.Title
      data-slot="dialog-title"
      className={cn("text-base font-semibold", className)}
      {...props}
    />
  );
}

export function AlertDialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Description>) {
  return (
    <AlertDialogPrimitive.Description
      data-slot="dialog-description"
      className={cn("text-sm text-muted-foreground", className)}
      {...props}
    />
  );
}

/** Rząd przycisków. Na wąskim ekranie w kolumnie — obok siebie byłyby za ciasne. */
export function AlertDialogFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn("mt-2 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end", className)}
      {...props}
    />
  );
}

export function AlertDialogCancel({
  className,
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Cancel>) {
  return (
    <AlertDialogPrimitive.Cancel
      data-slot="dialog-cancel"
      className={cn(buttonVariants({ variant: "outline" }), className)}
      {...props}
    />
  );
}

/**
 * Przycisk potwierdzenia. Domyślnie w wariancie niszczącym, bo to okno pojawia
 * się przy rzeczach nieodwracalnych — a kolor jest tu częścią ostrzeżenia.
 */
export function AlertDialogAction({
  className,
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Action>) {
  return (
    <AlertDialogPrimitive.Action
      data-slot="dialog-action"
      className={cn(buttonVariants({ variant: "destructive" }), className)}
      {...props}
    />
  );
}
